// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// The single temperature-anomaly ramp used IDENTICALLY across every view, so a
// colour always means "cooler / warmer than the 1961–1990 normal". Blue→red
// diverging (ColorBrewer RdBu family, the #ShowYourStripes idiom).

type RGB = [number, number, number];

// Cool → warm stops (RdBu reversed).
const DIVERGING: RGB[] = [
  [5, 48, 97], // deep blue
  [33, 102, 172],
  [67, 147, 195],
  [146, 197, 222],
  [209, 229, 240],
  [247, 247, 247], // near-white (≈ baseline)
  [253, 219, 199],
  [244, 165, 130],
  [214, 96, 77],
  [178, 24, 43],
  [103, 0, 31], // deep red
];

// Sequential warm ramp for always-positive quantities (warming rate, hot days).
const SEQ_WARM: RGB[] = [
  [255, 247, 236],
  [254, 217, 170],
  [253, 174, 107],
  [241, 129, 78],
  [217, 72, 45],
  [166, 24, 22],
  [103, 0, 13],
];

function lerp(a: RGB, b: RGB, t: number): RGB {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
}

function rampAt(stops: RGB[], t: number): string {
  const x = Math.max(0, Math.min(1, t)) * (stops.length - 1);
  const i = Math.min(stops.length - 2, Math.floor(x));
  const [r, g, b] = lerp(stops[i], stops[i + 1], x - i);
  return `rgb(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)})`;
}

/** Diverging colour for an anomaly (°C). `domain` is the ± clamp (default 1.6). */
export function anomalyColor(anom: number | null, domain = 1.6): string {
  if (anom == null || !Number.isFinite(anom)) return '#e5e7eb';
  return rampAt(DIVERGING, (anom + domain) / (2 * domain));
}

/** Sequential warm colour for a warming rate (°C/decade), clamped to [0, max]. */
export function rateColor(rate: number | null, max = 0.3): string {
  if (rate == null || !Number.isFinite(rate)) return '#e5e7eb';
  return rampAt(SEQ_WARM, rate / max);
}

/** Generic sequential-warm sampler for hot-day counts etc. */
export function warmColor(v: number | null, max: number): string {
  if (v == null || !Number.isFinite(v) || max <= 0) return '#e5e7eb';
  return rampAt(SEQ_WARM, v / max);
}

/** Legend stop swatches for the diverging ramp, from -domain to +domain. */
export function anomalyLegend(domain = 1.6, n = 7): Array<{ label: string; color: string }> {
  const out: Array<{ label: string; color: string }> = [];
  for (let i = 0; i < n; i++) {
    const a = -domain + (2 * domain * i) / (n - 1);
    out.push({ label: (a > 0 ? '+' : '') + a.toFixed(1), color: anomalyColor(a, domain) });
  }
  return out;
}
